import { cn } from "@/lib/utils";

/**
 * Fixed field-of-view arc of the view cone, in degrees. A visual affordance —
 * NOT a measured lens FOV (we don't capture real FOV). See [[directional-pins]].
 */
const CONE_ARC_DEGREES = 60;

/**
 * On-screen radius of the cone in pixels at `--pin-scale: 1`. Picked to fan
 * out past the ~40px pin marker without dominating it. The cone rides
 * `--pin-scale`, so this is its size at native zoom; it shrinks/grows inversely
 * with plan zoom to hold a constant on-screen length.
 */
const DEFAULT_RADIUS_PX = 56;

/**
 * SVG viewBox dimension. The wedge is drawn in a square box; the apex sits at
 * the box centre so the whole thing pivots cleanly around the pin.
 */
const VIEWBOX = 100;

export interface PinDirectionConeProps {
  /**
   * Plan-relative bearing in degrees, clockwise, 0° = drawing-top (see
   * [[directional-pins]]). `null`/`undefined` renders nothing — an undirected
   * pin. `360` is normalized to `0`.
   */
  bearing: number | null | undefined;
  /** On-screen radius in px at `--pin-scale: 1`. Defaults to {@link DEFAULT_RADIUS_PX}. */
  size?: number;
  /**
   * Positioning classes from the consumer — typically the same absolute
   * `left`/`top` placement the pin marker uses, so the cone anchors at the
   * pin centre. The component owns the centre/rotate/scale transform.
   */
  className?: string;
  /**
   * Render as a faint, **uncommitted preview** rather than a set direction —
   * the editing affordance shown at the handle's rest angle before the user
   * has aimed (see [[directional-pins]]). Purely visual: it does NOT set a
   * bearing. The consumer passes a rest bearing (drawing-top) so the wedge has
   * something to point at; the record's `pin_bearing` stays null until a real
   * drag. Defaults to `false` (a committed, full-strength cone).
   */
  preview?: boolean;
}

/**
 * Maps a polar angle (degrees, 0° = up, clockwise) to a point on the cone's
 * arc in SVG user units, with the apex at the box centre and the given
 * `radius`. `-90° + angle` puts 0° at the top and sweeps clockwise in SVG's
 * y-down coordinate space.
 */
function arcPoint(angleDeg: number, radius: number): readonly [number, number] {
  const rad = (-90 + angleDeg) * (Math.PI / 180);
  const cx = VIEWBOX / 2;
  const cy = VIEWBOX / 2;
  return [cx + radius * Math.cos(rad), cy + radius * Math.sin(rad)];
}

/**
 * The shared **view cone** — a translucent wedge fanning from a pin in the
 * bearing direction, the renderer for [[directional-pins]]. Bisector = bearing,
 * fixed ~60° arc, drawn beneath the pin marker (`pointer-events: none`) so the
 * marker stays the tap target.
 *
 * The wedge is drawn pointing straight UP in local SVG coordinates and the
 * whole element is rotated by `bearing`, so the on-element `rotate(...)`
 * transform value equals the bearing exactly. It holds a constant on-screen
 * size by riding the same `--pin-scale` var the pin marker uses
 * (`transformOrigin` at the pin centre, so it pivots around the pin).
 *
 * Fills the pin colour (`--pin`) at low opacity — a translucent blue matching a
 * directed pin's `ring-pin` ring, so the cone, ring, and photo read as one blue
 * directional marker (an undirected pin shows no cone at all).
 *
 * Renders `null` when `bearing` is null/undefined — an undirected pin shows no
 * cone, distinct from a `0°` bearing which points to drawing-top. With
 * `preview`, the wedge renders fainter to mark an **uncommitted** editing
 * preview (the consumer feeds a rest bearing); it still draws nothing for a
 * null bearing, so the preview is opt-in per render. See [[directional-pins]].
 */
export function PinDirectionCone({
  bearing,
  size = DEFAULT_RADIUS_PX,
  className,
  preview = false,
}: PinDirectionConeProps) {
  // Null/undefined → no direction → no cone. `0` is a real bearing and must
  // pass this guard (it is `!= null` but falsy), so test against null only.
  if (bearing == null) return null;

  // Normalize into `[0, 360)` so `360` becomes `0` and any out-of-range input
  // (e.g. a wrapped drag value) renders sensibly.
  const normalized = ((bearing % 360) + 360) % 360;

  // Wedge centred on "up" (0°) in local coords; the element rotation applies
  // the bearing. Half-arc either side of the bisector.
  const half = CONE_ARC_DEGREES / 2;
  const apex = VIEWBOX / 2;
  const radius = VIEWBOX / 2;
  const [x1, y1] = arcPoint(-half, radius);
  const [x2, y2] = arcPoint(half, radius);
  // Fixed ~60° arc is always < 180°, so large-arc-flag is 0; sweep-flag 1 is
  // the clockwise direction from the left edge to the right edge.
  const path = `M ${apex} ${apex} L ${x1} ${y1} A ${radius} ${radius} 0 0 1 ${x2} ${y2} Z`;

  // Diameter in px at `--pin-scale: 1`; the scale var shrinks/grows it with
  // zoom. The element is sized to the diameter and centred on the pin.
  const diameter = size * 2;

  return (
    <div
      data-slot="pin-direction-cone"
      aria-hidden
      className={cn("pointer-events-none absolute", className)}
      style={{
        width: diameter,
        height: diameter,
        // Centre on the pin, then rotate by the bearing, then ride --pin-scale.
        // transform-origin at the element centre = the pin anchor, so the cone
        // pivots around the pin at any zoom.
        transform: `translate(-50%, -50%) rotate(${normalized}deg) scale(var(--pin-scale, 1))`,
        transformOrigin: "center center",
      }}
    >
      <svg
        viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`}
        width={diameter}
        height={diameter}
        className="block overflow-visible"
        data-testid="pin-direction-cone-svg"
        data-bearing={normalized}
        data-preview={preview ? "true" : undefined}
      >
        <path
          d={path}
          className={cn(
            "fill-pin",
            // A committed direction reads at full strength; an uncommitted
            // preview is deliberately fainter so it never looks like a set
            // bearing (and an undirected pin stays visually undirected).
            preview ? "opacity-10" : "opacity-25",
          )}
        />
      </svg>
    </div>
  );
}
